import React from 'react'

/**
 * Sparkline — minimal inline-SVG line chart.
 *
 * No dependencies. Takes a series of numeric samples and renders a polyline
 * scaled to fit `width × height`. Nullish samples are dropped from the path
 * but reserve their X slot, so a gap in the data shows as a break in the
 * line instead of a 0-floor distortion.
 *
 * Why inline SVG vs. a chart library: the Observability brief locks in
 * "no Chart.js, no Recharts, no D3". The data shape is 1-D, ~150 samples,
 * already in memory — a polyline is the right tool.
 */

interface SparklineProps {
  /** Series of samples. `null`/`undefined` entries are rendered as gaps. */
  data: ReadonlyArray<number | null | undefined>
  /** Render width in px. Defaults to 140 to fit the StatsPanel card. */
  width?: number
  /** Render height in px. Defaults to 36. */
  height?: number
  /**
   * Fixed Y-axis minimum. When provided together with `max`, the chart
   * doesn't auto-rescale — useful for percent series (0..100) so a flat
   * line stays at the bottom instead of being amplified to fill the box.
   */
  min?: number
  /** Fixed Y-axis maximum. See `min`. */
  max?: number
  /** Stroke color. Defaults to currentColor so callers can use Tailwind text-*. */
  stroke?: string
  /** Stroke width in px. Defaults to 1.5. */
  strokeWidth?: number
  /** Optional data-testid forwarded to the root SVG. */
  testId?: string
  /** Optional accessible label. */
  ariaLabel?: string
}

export const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 140,
  height = 36,
  min,
  max,
  stroke = 'currentColor',
  strokeWidth = 1.5,
  testId,
  ariaLabel,
}) => {
  const numeric = data.map((v) =>
    typeof v === 'number' && Number.isFinite(v) ? v : null,
  )
  const finite = numeric.filter((v): v is number => v != null)

  // Defensive: empty / all-null series → draw nothing, but still render the
  // SVG box so the layout doesn't jump when the first sample arrives.
  if (finite.length === 0 || data.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        data-testid={testId}
        role="img"
        aria-label={ariaLabel ?? 'No data'}
        className="block"
      />
    )
  }

  const yMin = min ?? Math.min(...finite)
  const yMax = max ?? Math.max(...finite)
  // Avoid zero-range division: when all samples are identical, draw a flat
  // line through the middle of the box.
  const range = yMax - yMin === 0 ? 1 : yMax - yMin

  const stepX = data.length > 1 ? width / (data.length - 1) : width
  // Inset by half the stroke so the line doesn't get clipped at the edges.
  const pad = strokeWidth / 2

  // Build a single polyline with M/L commands; insert `M` after each gap so
  // the line breaks cleanly rather than crossing the chart on a null sample.
  let d = ''
  let pendingMove = true
  numeric.forEach((v, i) => {
    if (v == null) {
      pendingMove = true
      return
    }
    const x = i * stepX
    // Flip Y axis: SVG y grows downward; we want larger values higher up.
    const yNorm = (v - yMin) / range
    const y = height - pad - yNorm * (height - 2 * pad)
    d += `${pendingMove ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)} `
    pendingMove = false
  })

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      data-testid={testId}
      role="img"
      aria-label={ariaLabel ?? 'Time series'}
      className="block"
    >
      <path
        d={d.trim()}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

export default Sparkline
